/**
 * What each placement source is, in words a founder can act on (canon APP-006).
 *
 * Canon § 16 records a placement's source as one of four machine values, and a history that
 * printed `rollover` beside `auction` would be telling a founder nothing: the whole question
 * this panel answers is *what their credit bought*, and the answer differs by source.
 *
 *   auction    they won that day's bidding and the winning amount was consumed;
 *   rollover   they filled otherwise-unused Top 10 capacity at **no additional charge**
 *              (canon § 9), which must never read as a second charge;
 *   founding   cold-start exposure while eligible inventory was at most ten (canon § 6),
 *              also at no charge;
 *   honorable  a paid one-day listing **outside** the Top 10 that never rolls over
 *              (canon § 10) — and therefore never had a rank to report.
 *
 * The wording lives here rather than in the panel for the reason `./bid-messages.ts` gives
 * for refusals: it is the stable part of the contract keyed on a code, and one sentence per
 * source is easier to keep honest — and to test against canon — in one place than scattered
 * through a component's branches.
 *
 * None of these sentences promises anything canon does not. A Top 10 position is sponsored
 * placement bought at auction (canon § 12), never a judgement that the deal was better.
 */
import type { PlacementSource } from "../api/types";

export interface PlacementSourceCopy {
  /** The short name of the source, always shown as a word, never as a colour or mark. */
  label: string;
  /** One sentence saying what that source means, including what it cost. */
  note: string;
  /** Whether this source occupied a Top 10 position on its display day. */
  inTop10: boolean;
}

const COPY: Record<PlacementSource, PlacementSourceCopy> = {
  auction: {
    label: "Won at auction",
    note: "You won this Top 10 position in that day's auction. It is sponsored placement — it says you bid highest, not that Dealbid ranked your deal best.",
    inTop10: true,
  },
  rollover: {
    label: "Rolled over",
    note: "Top 10 capacity was left unfilled that day, so your deal was carried forward into it. A rollover costs nothing additional and consumed no credit.",
    inTop10: true,
  },
  founding: {
    label: "Founding placement",
    note: "Dealbid had ten or fewer eligible deals that day, so your Founding Deal stayed on the board rather than dropping off. It cost nothing additional and consumed no credit.",
    inTop10: true,
  },
  honorable: {
    label: "Honorable Mention",
    note: "A one-day listing outside the Top 10, in its own section of that day's board. It has no Top 10 position, and it ran for that day only.",
    inTop10: false,
  },
};

export function describePlacementSource(source: PlacementSource): PlacementSourceCopy {
  return COPY[source];
}
